import React, {useContext, useEffect, useMemo, useState, useCallback} from "react";
import usePA_GlobalStore from "../../Services/ZustandGlobalStore";
import {PAContext} from "../../Services/PAContext";

import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
    Box, Typography, Card, CardContent,
    Button, TextField, Modal, Select, MenuItem, FormControl, InputLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CardActions,
} from '@mui/material';
import { blue, green, red, grey } from '@mui/material/colors';

import currencyList from "../../sampleData/currencyList";

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Divider from "@mui/material/Divider";



// --- DAYJS UTILITY INCLUSION ---
dayjs.extend(relativeTime);

// --- CONSTANTS ---
const DARK_BLUE = '#10318f'; // Color(0xFF10318f) for Icon and Caption
const CATEGORY_COLORS = {
    'Meals': '#B3E0C6', // Soft Mint
    'Lodging': '#FAD7A0', // Soft Apricot
    'Activities': '#A9CCE3', // Light Blue
    'Flights': '#fdf2c0', // Soft Yellow
    'Supplies': '#F5B7B1', // Soft Coral
    'Incidentals': '#F8C471', // Medium Gold
    'Shopping': '#85C1E9', // Medium Blue
    'Transportation': '#85dfb1', // Soft Teal
    'General': '#D2B4DE', // Lavender
};
const CATEGORY_ICONS = {
    'Meals': 'icon-fork-knife2',
    'Lodging': 'icon-bed',
    'Activities': 'icon-list-heart',
    'Flights': 'icon-airplane',
    'Supplies': 'icon-checklist2',
    'Incidentals': 'icon-flag2',
    'Shopping': 'icon-bag',
    'Transportation': 'icon-car',
    'General': 'icon-helm',
};
const CATEGORIES = Object.keys(CATEGORY_COLORS);
const DEFAULT_CURRENCY_CODE = 'USD';

// --- BUDGET UNIT OPTIONS ---
const unitOptions = {
    Meals: ['Each Meal', 'Per Day', 'For Entire Event'],
    Lodging: ['Each Lodging', 'Per Night', 'For Entire Event'],
    Activities: ['Each Activity', 'Per Day', 'For Entire Event'],
    Flights: ['Per Flight', 'Roundtrip', 'For Entire Event'],
    Supplies: ['Each Receipt', 'Per Day', 'For Entire Event'],
    Incidentals: ['Each Receipt', 'Per Day', 'For Entire Event'],
    Shopping: ['Each Receipt', 'Per Day', 'For Entire Event'],
    Transportation: ['Each Receipt', 'Per Day', 'For Entire Event'],
    General: ['Each Receipt', 'Per Day', 'For Entire Event']
};

// --- UTILITIES ---
const formatCurrency = (amount, currencyCode = 'USD') => {
    // 1. Get the symbol to append.
    const symbol = currencyList.find(c => c.code === currencyCode)?.symbol || currencyCode;

    // 2. 🟢 FIXED: Force the formatting engine to maintain exactly 2 decimal points
    const numberFormatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2, // Ensures trailing .00 if it's a whole number
        maximumFractionDigits: 2, // Prevents rounding past 2 decimal digits
    });

    const numberPart = numberFormatter.format(Math.abs(amount));

    // 3. Handle negative numbers by adding the sign prefix.
    const sign = amount < 0 ? '-' : '';

    // 4. Return the format: [SIGN][NUMBER] [SYMBOL]
    return `${symbol}${sign}${numberPart} ${currencyCode}`;
};

const sumNumbers = (numbers) =>
    numbers.reduce((sum, num) => sum + (num || 0), 0);


// --- DATA MAPPING FUNCTIONS ---

const mapStateToAttendeeBudget = (updatedAppState) => {
    // Initialize the root schema fields matching your MongoDB sample
    const flatBudget = {
        total_budget: parseFloat(updatedAppState.spendingPool) || 0,
        currency: updatedAppState.currentCurrencyCode || "USD",
        private: false
    };

    // Dictionary mapping UI category names to your exact database field prefixes
    const categoryMapping = {
        'Meals': 'meal',
        'Lodging': 'lodging',
        'Flights': 'flight',
        'Transportation': 'transportation',
        'Activities': 'activity',
        'Shopping': 'shopping',
        'Supplies': 'supplies',
        'Incidentals': 'incidentals',
        'General': 'general',
    };

    // Loop through and uncoil the nested maps into flat root keys
    Object.keys(categoryMapping).forEach(uiCategory => {
        const dbPrefix = categoryMapping[uiCategory];

        // Maps e.g. appState.categoryLimits['Meals'] -> flatBudget.meal_budget
        flatBudget[`${dbPrefix}_budget`] = parseFloat(updatedAppState.categoryLimits[uiCategory]) || 0;

        // Maps e.g. appState.categoryUnits['Meals'] -> flatBudget.meal_budget_unit
        flatBudget[`${dbPrefix}_budget_unit`] = updatedAppState.categoryUnits[uiCategory] || "Each Receipt";
    });

    return flatBudget;
};

const mapAttendeeBudgetToState = (flatBudget) => {
    // 1. Establish clean UI state defaults
    const state = {
        spendingPool: flatBudget?.total_budget || 0,
        currentCurrencyCode: flatBudget?.currency || "USD",
        categoryLimits: {},
        categoryUnits: {}
    };

    const categoryMapping = {
        'Meals': 'meal',
        'Lodging': 'lodging',
        'Flights': 'flight',
        'Transportation': 'transportation',
        'Activities': 'activity',
        'Shopping': 'shopping',
        'Supplies': 'supplies',
        'Incidentals': 'incidentals',
        'General': 'general',
    };

    // 2. Reconstruct the UI's nested maps from the database's flat root keys
    Object.keys(categoryMapping).forEach(uiCategory => {
        const dbPrefix = categoryMapping[uiCategory];

        // Pulls flatBudget.meal_budget -> state.categoryLimits['Meals']
        state.categoryLimits[uiCategory] = flatBudget?.[`${dbPrefix}_budget`] || 0;

        // Pulls flatBudget.meal_budget_unit -> state.categoryUnits['Meals']
        state.categoryUnits[uiCategory] = flatBudget?.[`${dbPrefix}_budget_unit`] || "Each Receipt";
    });

    return state;
};

// --- MUI STYLING FOR MODAL CONTENT ---
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 400 },
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
};


const SpendingPoolModal = ({
                               isOpen,
                               spendingPool,
                               currentCurrencyCode,
                               currencyList,
                               onClose,
                               onSave,
                               modalStyle // Pass your glassmorphic or layout style object down
                           }) => {
    const [localPoolValue, setLocalPoolValue] = useState('');
    const [localCurrencyCode, setLocalCurrencyCode] = useState('');

    // Dynamically sync fields ONLY when the modal transitions open
    useEffect(() => {
        if (isOpen) {
            setLocalPoolValue(String(spendingPool ?? ''));
            setLocalCurrencyCode(currentCurrencyCode ?? 'USD');
        }
    }, [isOpen, spendingPool, currentCurrencyCode]);

    if (!isOpen) return null;

    // Find the symbol for display in the input label securely from props
    const currencySymbolForInput = currencyList.find(opt => opt.code === localCurrencyCode)?.symbol || localCurrencyCode;

    const handleLocalSave = () => {
        onSave(localPoolValue, localCurrencyCode);
    };

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            aria-labelledby="pool-modal-title"
            // Programmatic blocks to strip MUI of aggressive focus hijacking on mobile
            disableEnforceFocus={true}
            disableAutoFocus={true}
        >
            <Box sx={modalStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography id="pool-modal-title" variant="h6" component="h2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                        Edit Total Spending Pool
                    </Typography>
                </Box>

                <Typography sx={{ color: 'text.secondary', mb: 3 }} variant="body2">
                    Set the maximum total amount you are willing to spend across all categories and select your currency.
                </Typography>

                {/* Currency Selection Dropdown */}
                <FormControl fullWidth margin="normal" variant="outlined">
                    <InputLabel id="currency-select-label">Currency</InputLabel>
                    <Select
                        labelId="currency-select-label"
                        value={localCurrencyCode}
                        onChange={e => setLocalCurrencyCode(e.target.value)}
                        label="Currency"
                    >
                        {currencyList.map(option => (
                            <MenuItem key={option.code} value={option.code}>
                                {option.symbol} - {option.name} ({option.code})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Pool Amount Input */}
                <TextField
                    fullWidth
                    label={`New Total Pool Amount (${currencySymbolForInput})`}
                    // 🚨 BYPASS NATIVE MOBILE CHROMIUM/SAFARI SPINNER LOOPS
                    type="text"
                    inputMode="decimal"
                    value={localPoolValue}
                    onChange={e => {
                        const val = e.target.value;
                        // Permit numbers and a lone dot context cleanly
                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                            setLocalPoolValue(val);
                        }
                    }}
                    margin="normal"
                    variant="outlined"
                    sx={{ mb: 3 }}
                    // Total event lockdown against parent frame interference
                    onTouchStart={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button onClick={onClose} variant="outlined" color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleLocalSave} variant="contained" color="primary">
                        Save Pool
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};


// UI Component for Editing Category Budget
const EditBudgetCategoryModal = ({
                                     isOpen,
                                     category,
                                     categoryLimits,
                                     categoryUnits,
                                     currentCurrencySymbol,
                                     onClose,
                                     onSave,
                                     unitOptions,
                                     categoryColors
                                 }) => {
    // Initialize states cleanly with static blank fallbacks
    const [localLimitValue, setLocalLimitValue] = useState('');
    const [localUnit, setLocalUnit] = useState('');

    // Sync state dynamically ONLY when the modal transitions from closed to open
    useEffect(() => {
        if (isOpen && category) {
            const limit = categoryLimits[category] || 0;
            const unit = categoryUnits[category] || unitOptions[category]?.[unitOptions[category].length - 1] || '';
            setLocalLimitValue(String(limit));
            setLocalUnit(unit);
        }
    }, [isOpen, category, categoryLimits, categoryUnits, unitOptions]);

    if (!isOpen || !category) {
        return null;
    }

    const color = categoryColors[category] || '#000';
    const limitLabel = localUnit === 'For Entire Event'
        ? `Fixed Budget Limit (${currentCurrencySymbol || '$'})`
        : `Per Unit Limit (${currentCurrencySymbol || '$'})`;

    const handleLocalSave = () => {
        onSave(category, localLimitValue, localUnit);
    };

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            aria-labelledby="category-modal-title"
            // Hard programmatic blocks to disable Material-UI focus traps
            disableEnforceFocus={true}
            disableAutoFocus={true}
        >
            <Box sx={modalStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography
                        id="category-modal-title"
                        variant="h6"
                        component="h2"
                        style={{ color }}
                        sx={{ fontWeight: 700 }}
                    >
                        Set Budget for {category}
                    </Typography>
                </Box>

                <Typography sx={{ color: 'text.secondary', mb: 3 }} variant="body2">
                    Enter the new budget limit and select the unit format configuration for the{' '}
                    <Box component="span" style={{ color }} sx={{ fontWeight: 600 }}>
                        {category}
                    </Box>{' '}
                    category grid item.
                </Typography>

                <FormControl fullWidth margin="normal" variant="outlined">
                    <InputLabel id="unit-select-label">Budget Unit</InputLabel>
                    <Select
                        labelId="unit-select-label"
                        value={localUnit}
                        onChange={e => setLocalUnit(e.target.value)}
                        label="Budget Unit"
                    >
                        {unitOptions[category]?.map(unit => (
                            <MenuItem key={unit} value={unit}>
                                {unit}
                            </MenuItem>
                        )) || <MenuItem value={localUnit}>{localUnit}</MenuItem>}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    label={limitLabel}
                    // Bypasses input validation loops on mobile layout triggers
                    type="text"
                    inputMode="decimal"
                    value={localLimitValue}
                    onChange={e => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d*$/.test(val)) setLocalLimitValue(val);
                    }}
                    margin="normal"
                    variant="outlined"
                    sx={{ mb: 3 }}
                    // Strict event isolation for mobile viewports
                    onTouchStart={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button onClick={onClose} variant="outlined" color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleLocalSave} variant="contained" color="secondary">
                        Save Budget
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

function UnifiedBudgetPersonalBudget() {

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account } = dataArray;

    const currentAttendee = useMemo(() => {
        return PA_GlobalObject?.Planning?.attendees?.find(a => a.id === Account.id);
    }, [PA_GlobalObject.Planning.attendees, Account.id]);

    const attendeeBudget = currentAttendee?.budget;
    const initialState = useMemo(() => mapAttendeeBudgetToState(attendeeBudget), [attendeeBudget]);

    // Main local component view state management (Budget parameters only!)
    const [appState, setAppState] = useState({
        ...initialState,
    });

    // Sync budget properties from global state changes
    useEffect(() => {
        setAppState(prev => ({
            ...prev,
            ...mapAttendeeBudgetToState(attendeeBudget)
        }));
    }, [attendeeBudget]);

    // 🟢 NEW: Gather and normalize all expenses from different event structures
    // 🟢 UPDATED: Gather and normalize all expenses with extra metadata captures
    const combinedAttendeeExpenses = useMemo(() => {
        const expensesList = [];
        const planning = PA_GlobalObject?.Planning;
        if (!planning) return expensesList;

        const userIdStr = String(Account.id);

        // LOCAL HELPER: Resolves the root trip plan date if an individual item lacks a timestamp
        const getRootPlanFallbackDate = () => {
            if (planning.time_table?.dates?.[0]?.date) {
                const planDateObj = planning.time_table.dates[0];
                const planTimeStr = planDateObj.begin_time ? ` ${planDateObj.begin_time}` : '';
                return new Date(`${planDateObj.date}${planTimeStr}`).toISOString();
            }
            return new Date().toISOString();
        };

        // 1. Extract Meals Cost
        if (Array.isArray(planning.meals)) {
            planning.meals.forEach(meal => {
                const myAllocation = meal.attendees?.find(att => String(att.id) === userIdStr);
                if (myAllocation && (parseFloat(myAllocation.cost) > 0)) {

                    const resolvedDescription = meal.location?.name || meal.title || meal.name || 'Event Meal';

                    let resolvedTimestamp = null;
                    const timeTable = meal.time_table;

                    if (timeTable) {
                        if (Array.isArray(timeTable.dates) && timeTable.dates.length > 0 && timeTable.dates[0].date) {
                            const firstDateObj = timeTable.dates[0];
                            const timeStr = firstDateObj.begin_time ? ` ${firstDateObj.begin_time}` : '';
                            resolvedTimestamp = new Date(`${firstDateObj.date}${timeStr}`).toISOString();
                        }
                    }

                    if (!resolvedTimestamp && meal.created) {
                        resolvedTimestamp = new Date(meal.created).toISOString();
                    }

                    if (!resolvedTimestamp && planning.time_table?.dates?.[0]?.date) {
                        const planDateObj = planning.time_table.dates[0];
                        const planTimeStr = planDateObj.begin_time ? ` ${planDateObj.begin_time}` : '';
                        resolvedTimestamp = new Date(`${planDateObj.date}${planTimeStr}`).toISOString();
                    }

                    if (!resolvedTimestamp || isNaN(Date.parse(resolvedTimestamp))) {
                        resolvedTimestamp = new Date().toISOString();
                    }

                    expensesList.push({
                        id: `meal-${meal.id || Math.random()}`,
                        category: 'Meals',
                        itemDescription: resolvedDescription,
                        amount: parseFloat(myAllocation.cost) || 0,
                        timestamp: resolvedTimestamp,
                        color: CATEGORY_COLORS['Meals'],
                        // 🟢 ADDED EXTRA FIELDS & OPTIONAL CHAINING PROTECTION:
                        merchant: meal.merchant || meal.location?.name || null,
                        notes: meal.notes || meal.description || null,
                        location: meal.location || null
                    });
                }
            });
        }

        // 2. Extract Lodging Cost
        if (Array.isArray(planning.lodging)) {
            planning.lodging.forEach(lodge => {
                const myAllocation = lodge.attendees?.find(att => String(att.id) === userIdStr);
                if (myAllocation && (parseFloat(myAllocation.cost) > 0)) {
                    expensesList.push({
                        id: `lodging-${lodge.id || Math.random()}`,
                        category: 'Lodging',
                        itemDescription: lodge.name || 'Accommodations',
                        amount: parseFloat(myAllocation.cost) || 0,
                        timestamp: lodge.check_in || getRootPlanFallbackDate(),
                        color: CATEGORY_COLORS['Lodging'],
                        // 🟢 ADDED EXTRA FIELDS:
                        merchant: lodge.merchant || lodge.name || null,
                        notes: lodge.notes || lodge.description || null,
                        location: lodge.location || null
                    });
                }
            });
        }

        // 3. Extract Activities Cost
        if (Array.isArray(planning.activities)) {
            planning.activities.forEach(act => {
                const myAllocation = act.attendees?.find(att => String(att.id) === userIdStr);
                if (myAllocation && (parseFloat(myAllocation.cost) > 0)) {
                    expensesList.push({
                        id: `activity-${act.id || Math.random()}`,
                        category: 'Activities',
                        itemDescription: act.title || 'Event Activity',
                        amount: parseFloat(myAllocation.cost) || 0,
                        timestamp: act.start_time || getRootPlanFallbackDate(),
                        color: CATEGORY_COLORS['Activities'],
                        // 🟢 ADDED EXTRA FIELDS:
                        merchant: act.merchant || act.title || null,
                        notes: act.notes || act.description || null,
                        location: act.location || null
                    });
                }
            });
        }

        // 4. Extract Flights Cost
        if (Array.isArray(planning.flights)) {
            planning.flights.forEach(flight => {
                if (flight.attendee && String(flight.attendee.id) === userIdStr && (parseFloat(flight.attendee.cost) > 0)) {
                    expensesList.push({
                        id: `flight-${flight.id || Math.random()}`,
                        category: 'Flights',
                        itemDescription: `${flight.airline_name || 'Flight'} (${flight.ident || 'TBD'})`,
                        amount: parseFloat(flight.attendee.cost) || 0,
                        timestamp: flight.depart_timestamp || getRootPlanFallbackDate(),
                        color: CATEGORY_COLORS['Flights'],
                        // 🟢 ADDED EXTRA FIELDS:
                        merchant: flight.airline_name || null,
                        notes: flight.notes || null,
                        location: null
                    });
                }
            });
        }

        // 5. Extract Flat Receipts Sections
        if (Array.isArray(planning.receipts)) {
            planning.receipts.forEach(receipt => {
                if (String(receipt.attendee_id) === userIdStr && parseFloat(receipt.amount) > 0) {

                    if (receipt.section_item && String(receipt.section_item).trim() !== "") {
                        return;
                    }

                    const sectionCategory = CATEGORIES.includes(receipt.section) ? receipt.section : 'General';
                    expensesList.push({
                        id: receipt.id || `receipt-${Math.random()}`,
                        category: sectionCategory,
                        itemDescription: receipt.itemDescription || receipt.merchant || 'Logged Receipt',
                        amount: parseFloat(receipt.amount),
                        timestamp: receipt.timestamp || getRootPlanFallbackDate(),
                        color: CATEGORY_COLORS[sectionCategory],
                        // 🟢 ADDED EXTRA FIELDS TO ASSIST TABLE RENDERING:
                        merchant: receipt.merchant || null,
                        notes: receipt.notes || receipt.description || null,
                        location: receipt.location || null
                    });
                }
            });
        }

        return expensesList;
    }, [PA_GlobalObject?.Planning, Account.id]);


    const [isEditingPool, setIsEditingPool] = useState(false);
    const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [activeSegment, setActiveSegment] = useState(null);
    const [filterCategory, setFilterCategory] = useState('All');
    const [sortKey, setSortKey] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('desc');
    const [pendingBudgetAction, setPendingBudgetAction] = useState(null);

    const currentCurrencyCode = appState.currentCurrencyCode;
    const currentCurrencySymbol = currencyList.find(c => c.code === currentCurrencyCode)?.symbol || '$';


    // --- PARENT STATE PERSISTENCE LOGIC ---

    const handleUpdateParentBudget = useCallback((updatedAppState) => {
        const planningId = PA_GlobalObject?.Planning?.id;
        const attendeeId = Account?.id;

        if (!planningId || !attendeeId) return;

        // 🟢 FIXED: Generate the flat property payload matching your MongoDB structure
        const updatedBudgetPayload = mapStateToAttendeeBudget(updatedAppState);

        // 2. Update local Zustand state instantly so UI responds with zero lag
        set_PA_GlobalObject(prev => {
            const updatedAttendees = (prev?.Planning?.attendees || []).map(att => {
                if (att.id === attendeeId) {
                    return {
                        ...att,
                        budget: {
                            ...att.budget,
                            ...updatedBudgetPayload // Stores flat data locally
                        }
                    };
                }
                return att;
            });

            return {
                ...prev,
                Planning: {
                    ...prev.Planning,
                    attendees: updatedAttendees
                }
            };
        });

        // 3. Send over the WebSocket
        if (PA_GlobalObject?.Planning?.status !== 10) {
            const _sendString = {
                action: "E_updateEventAttendeeBudget",
                event_id: planningId,
                budget: updatedBudgetPayload // Pure flat JSON passthrough
            };

            console.log("Sending flat budget payload to socket channel:", _sendString);
            sendSocketMessage(_sendString);
        }

    }, [Account?.id, PA_GlobalObject?.Planning?.id, PA_GlobalObject?.Planning?.status, set_PA_GlobalObject, sendSocketMessage]);

    const totalBudgeted = useMemo(() => sumNumbers(Object.values(appState.categoryLimits)), [appState.categoryLimits]);
    const availablePool = appState.spendingPool;

    // 🟢 FIXED: Reference combinedAttendeeExpenses directly
    const totalSpent = useMemo(() => sumNumbers(combinedAttendeeExpenses.map(d => d.amount)), [combinedAttendeeExpenses]);

    const remainingTotal = useMemo(() => {
        return availablePool - totalSpent;
    }, [availablePool, totalSpent]);


    // Group expenses for chart and summary
    const expenseSummary = useMemo(() => {
        // 🟢 FIXED: reference combinedAttendeeExpenses
        const categorySpending = combinedAttendeeExpenses.reduce((acc, item) => {
            acc[item.category] = (acc[item.category] || 0) + item.amount;
            return acc;
        }, {});

        return CATEGORIES.map(category => {
            const perUnitLimit = appState.categoryLimits[category] || 0;
            const spent = categorySpending[category] || 0;
            const unit = appState.categoryUnits[category];

            // 🟢 FIXED: reference combinedAttendeeExpenses
            const categoryExpenses = combinedAttendeeExpenses.filter(e => e.category === category);

            let unitCount = 0;
            let effectiveLimit = 0;
            let overrunCount = 0;

            if (unit === 'For Entire Event') {
                unitCount = 1;
                effectiveLimit = perUnitLimit;
                overrunCount = 0;
            } else {
                unitCount = categoryExpenses.length;
                effectiveLimit = perUnitLimit * unitCount;
                overrunCount = categoryExpenses.filter(expense => expense.amount > perUnitLimit).length;
            }

            return {
                category,
                perUnitLimit,
                unitCount,
                limit: effectiveLimit,
                spent,
                balance: effectiveLimit - spent,
                unit,
                overrunCount,
                color: CATEGORY_COLORS[category],
                icon: CATEGORY_ICONS[category]
            };
        }).sort((a, b) => a.category.localeCompare(b.category));
    }, [combinedAttendeeExpenses, appState.categoryLimits, appState.categoryUnits]);


    // Filtered and Sorted Expenses for the Table/List
    const filteredAndSortedExpenses = useMemo(() => {
        // 🟢 FIXED: reference combinedAttendeeExpenses
        let filtered = [...combinedAttendeeExpenses];

        if (filterCategory !== 'All') {
            filtered = filtered.filter(exp => exp.category === filterCategory);
        }

        filtered.sort((a, b) => {
            let comparison = 0;
            if (sortKey === 'timestamp') {
                comparison = dayjs(a.timestamp).isBefore(dayjs(b.timestamp)) ? -1 : 1;
            } else if (sortKey === 'amount') {
                comparison = a.amount - b.amount;
            }
            return sortDirection === 'asc' ? comparison : comparison * -1;
        });

        return filtered.map(expense => {
            const summaryItem = expenseSummary.find(s => s.category === expense.category);
            return {
                ...expense,
                isOverLimit: summaryItem && expense.amount > summaryItem.perUnitLimit,
                perUnitLimit: summaryItem?.perUnitLimit,
            };
        });
    }, [combinedAttendeeExpenses, filterCategory, sortKey, sortDirection, expenseSummary]);


    // Data for the Pie Chart
    const chartData = useMemo(() => {
        return expenseSummary
            .filter(item => item.spent > 0)
            .map(item => ({
                name: item.category,
                value: item.spent,
                color: item.color,
                icon: item.icon,
            }));
    }, [expenseSummary]);

    // --- LOCAL STATE WRITE OPERATIONS ---

    const handleSetSpendingPool = useCallback((newPool, newCurrencyCode) => {
        const poolValue = parseFloat(newPool);
        if (isNaN(poolValue) || poolValue < 0) return;

        setAppState(prev => {
            const updatedState = {
                ...prev,
                spendingPool: poolValue,
                currentCurrencyCode: newCurrencyCode ?? prev.currentCurrencyCode
            };
            // Persist the change
            handleUpdateParentBudget(updatedState);
            return updatedState;
        });

        setIsEditingPool(false);
    }, [handleUpdateParentBudget]);


    const setCategoryLimitAndUnitInLocalState = useCallback((category, limit, unit) => {
        setAppState(prev => {
            const updatedState = {
                ...prev,
                categoryLimits: {
                    ...prev.categoryLimits,
                    [category]: limit
                },
                categoryUnits: {
                    ...prev.categoryUnits,
                    [category]: unit
                }
            };
            // Persist the change
            handleUpdateParentBudget(updatedState);
            return updatedState;
        });
    }, [handleUpdateParentBudget]);

    // Handler for saving the limit from the modal
    const handleSaveNewLimit = useCallback((category, newLimit, newUnit) => {
        const limitValue = parseFloat(newLimit);

        if (isNaN(limitValue) || limitValue < 0 || !category) return;

        // NOTE: The check against availablePool below uses the SUM of PER UNIT limits,
        // NOT the dynamically calculated EFFECTIVE limits, to determine if the pool is sufficient
        // for the user's *planned* per-unit budgets. This is a deliberate simplification.

        const currentLimitForCategory = appState.categoryLimits[category] || 0;
        const currentTotalBudgeted = totalBudgeted;

        const proposedTotalBudgeted = currentTotalBudgeted - currentLimitForCategory + limitValue;

        if (proposedTotalBudgeted > availablePool) {
            const requiredBudget = proposedTotalBudgeted;
            setPendingBudgetAction({
                category,
                limit: limitValue,
                unit: newUnit,
                requiredBudget,
                proposedTotal: proposedTotalBudgeted
            });
            return;
        }

        setCategoryLimitAndUnitInLocalState(category, limitValue, newUnit);

        setIsBudgetModalOpen(false);
        setEditingCategory(null);
    }, [appState.categoryLimits, totalBudgeted, availablePool, setCategoryLimitAndUnitInLocalState]);

    const increaseSpendingPoolAndSetLimit = useCallback(() => {
        if (!pendingBudgetAction) return;

        const { category, limit, unit, requiredBudget } = pendingBudgetAction;

        // 1. Update Pool and Currency
        handleSetSpendingPool(requiredBudget, currentCurrencyCode);

        // 2. Update Category Limit and Unit
        setCategoryLimitAndUnitInLocalState(category, limit, unit);

        setPendingBudgetAction(null);
        setIsBudgetModalOpen(false);
        setEditingCategory(null);
    }, [pendingBudgetAction, handleSetSpendingPool, setCategoryLimitAndUnitInLocalState, currentCurrencyCode]);


    // --- SUB-COMPONENTS (Unchanged) ---

    // Custom Tooltip for the Pie Chart
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const IconComponent = data.icon;
            const percentage = (totalSpent > 0 ? (data.value / totalSpent) * 100 : 0).toFixed(1);
            return (
                <Box sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: data.color }}>
                        {/* Lucide icons require a size, unlike MUI icons that use 'sx' */}
                        <IconComponent size={20} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{data.name}</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: data.color }}>
                        {formatCurrency(payload[0].value, currentCurrencyCode)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
                        {percentage}% of total expenses
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    const handleCardClick = (category) => {
        setEditingCategory(category);
        setIsBudgetModalOpen(true);
    };

    /**
     * Component for the Category Budget Card.
     */
    const CategoryBudgetCard = ({ item }) => {
        const hasLimit = item.perUnitLimit > 0;
        const isFixedLimit = item.unit === 'For Entire Event';

        // The limit against which we calculate progress
        const progressLimit = isFixedLimit ? item.perUnitLimit : item.limit;

        // Progress percentage
        const spentPercentage = progressLimit > 0 ? (item.spent / progressLimit) * 100 : (item.spent > 0 ? 100 : 0);
        const progressValue = Math.min(spentPercentage, 100);

        // Check for overspent status against the EFFECTIVE limit total spending
        const isOverspent = progressLimit > 0 && item.spent > progressLimit;

        // 🟡 WARNING CONDITION: Within 15% of the limit (i.e., spent 85% or more, but not yet overspent)
        const isNearLimitWarning = hasLimit && !isOverspent && spentPercentage >= 85;

        // Only evaluate sub-receipt anomalies if we aren't using a fixed lump-sum limit
        const isIndividuallyOverrun = !isFixedLimit && item.overrunCount > 0;

        // Determine the precise state color for the progress bar tracker
        let activeProgressColor = '#2e7d32'; // Default: Green
        let balanceColor = 'success.main';  // Default text: Green

        if (isOverspent || isIndividuallyOverrun) {
            activeProgressColor = '#d32f2f'; // Red
            balanceColor = 'error.main';
        } else if (isNearLimitWarning) {
            activeProgressColor = '#f57c00'; // Orange warning color
            balanceColor = 'warning.main';
        }

        const effectiveLimitDisplay = hasLimit
            ? formatCurrency(progressLimit, currentCurrencyCode)
            : 'N/A';

        const balanceAmount = progressLimit - item.spent;
        const balanceDisplay = hasLimit
            ? formatCurrency(Math.abs(balanceAmount), currentCurrencyCode)
            : 'N/A';

        const unitContext = item.unitCount > 0 && !isFixedLimit && hasLimit && (
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                ({formatCurrency(item.perUnitLimit, currentCurrencyCode)} x {item.unitCount} units)
            </Typography>
        );

        const noLimitText = hasLimit ? (
            isOverspent ? `${balanceDisplay} Over` : `${balanceDisplay} Left`
        ) : (
            'No Limit Set'
        );

        const balanceContent = (
            <Typography variant="subtitle2" sx={{ color: hasLimit ? balanceColor : 'text.secondary', fontWeight: 700 }}>
                {noLimitText}
            </Typography>
        );

        return (
            <Card
                onClick={() => handleCardClick(item.category)}
                sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                        boxShadow: 6,
                    },
                    borderRadius: 3,
                }}
            >
                {/* Vertical Progress Bar Container */}
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    height: '100%',
                    width: '8px',
                    bgcolor: '#f5f5f5',
                }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            width: '100%',
                            height: `${progressValue}%`,
                            bgcolor: activeProgressColor,
                            transition: 'height 0.7s ease-out',
                            borderTopLeftRadius: progressValue > 98 ? 0 : 2,
                            borderTopRightRadius: progressValue > 98 ? 0 : 2,
                        }}
                    />
                </Box>

                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pr: 4 }}>
                    {/* Header (Category Name) */}
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                        <i
                            className={item.icon}
                            style={{fontSize: '16px', color: DARK_BLUE}}
                        />
                        <Typography variant="overline" component="h4" sx={{color: DARK_BLUE, fontWeight: 700, paddingLeft:'5px'}}>
                            {item.category}
                        </Typography>
                    </Box>

                    {/* Main Focal Point (Total Spent and Category Limit) */}
                    <Box sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', py: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {formatCurrency(item.spent, currentCurrencyCode)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                            of {effectiveLimitDisplay}
                        </Typography>

                        {unitContext}
                    </Box>
                </CardContent>

                <CardActions>
                    <Box
                        sx={{
                            width: '100%',
                            textAlign: 'right',
                            pt: 0.5,
                            pr: 1,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        {balanceContent}
                    </Box>
                </CardActions>
            </Card>
        );
    };

    // UI Component for the Budget Exceed Modal
    const BudgetExceedModal = () => {
        if (!pendingBudgetAction) return null;

        const { requiredBudget, proposedTotal } = pendingBudgetAction;

        return (
            <Modal
                open={!!pendingBudgetAction}
                onClose={() => setPendingBudgetAction(null)}
                aria-labelledby="budget-exceed-modal"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'error.main' }}>
                        <Typography id="budget-exceed-modal" variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                            Budget Constraint Violation
                        </Typography>
                    </Box>

                    <Typography sx={{ color: 'text.primary', mb: 2 }}>
                        The total sum of all category Budgets (**{formatCurrency(proposedTotal, currentCurrencyCode)}**) is attempting to exceed the
                        Current Spending Pool (**{formatCurrency(availablePool, currentCurrencyCode)}**).
                    </Typography>

                    <Box sx={{ bgcolor: red[50], p: 2, borderRadius: 1, border: `1px solid ${red[200]}`, mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>Action Required:</Typography>
                        <Box sx={{ mt: 1, fontSize: 14, display: 'flex', justifyContent: 'space-between' }}>
                            <Typography color="text.secondary">New Pool Amount Required:</Typography>
                            <Typography sx={{ fontWeight: 800, color: 'error.dark' }}>{formatCurrency(requiredBudget, currentCurrencyCode)}</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setPendingBudgetAction(null)} variant="outlined" color="inherit">
                            Cancel Budget Change
                        </Button>
                        <Button onClick={increaseSpendingPoolAndSetLimit} variant="contained" color="error">
                            Adjust Pool & Set Budget
                        </Button>
                    </Box>
                </Box>
            </Modal>
        );
    };

    // Mobile-optimized card view for a single expense item.
    const MobileExpenseCard = ({ expense, currencyCode }) => {
        const isOverLimit = expense.isOverLimit;

        return (
            <Card sx={{
                mb: 1.5,
                borderRadius: 2,
                borderLeft: `8px solid ${expense.color}`,
            }}>
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>

                    {/* 1. Category (Left) and Date (Right) on the top line */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <i className={CATEGORY_ICONS[expense.category]} style={{color: DARK_BLUE}}/>
                            <Typography variant="caption" sx={{color: DARK_BLUE}}>
                                {expense.category}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {dayjs(expense.timestamp).format('MMM D, YYYY')}
                            </Typography>
                        </Box>
                    </Box>

                    {/* 2. Expense Name */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, textAlign: 'left' }}>
                        {expense.itemDescription}
                    </Typography>

                    {/* 3. Expense Amount */}
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 2 }}>
                        {formatCurrency(expense.amount, currencyCode)}
                    </Typography>

                    <Divider sx={{ mb: 1.5 }} />

                    {/* 4. Overlimit warning display layout indicator */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {
                            isOverLimit ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 0.5 }}>
                                    <i className="icon-warning" style={{ height: '24px', color: '#d32f2f' }} />
                                    {/* 🟢 FIXED: Swap currentCurrencyCode to local functional parameter currencyCode */}
                                    <Typography sx={{ fontSize: '11px', mt: '3px', textAlign: 'center', color: '#d32f2f' }}>
                                        Limit {formatCurrency(expense.perUnitLimit, currencyCode)}
                                    </Typography>
                                </Box>
                            ) : (
                                <i className="icon-check" style={{ height: '24px', color: 'rgb(56, 142, 60)' }} />
                            )
                        }
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const isOverAllocated = remainingTotal < 0;
    const poolBorder = isOverAllocated ? red[500] : green[500];
    const poolIconColor = isOverAllocated ? red[700] : green[700];

    useEffect(() => {
        console.log("appState : ", appState);
    }, [appState]);




    return (
        <Box>

            {/* Modals */}
            <BudgetExceedModal />
            <SpendingPoolModal
                isOpen={isEditingPool}
                spendingPool={appState.spendingPool}
                currentCurrencyCode={appState.currentCurrencyCode}
                currencyList={currencyList} // passing global data array down
                onClose={() => setIsEditingPool(false)}
                onSave={handleSetSpendingPool}
                modalStyle={modalStyle}
            />
            <EditBudgetCategoryModal
                isOpen={isBudgetModalOpen}
                category={editingCategory}
                categoryLimits={appState.categoryLimits}
                categoryUnits={appState.categoryUnits}
                currentCurrencySymbol={currentCurrencySymbol}
                onClose={() => {
                    setIsBudgetModalOpen(false);
                    setEditingCategory(null);
                }}
                onSave={handleSaveNewLimit}
                unitOptions={unitOptions}         // Pass global constants explicitly
                categoryColors={CATEGORY_COLORS}   // Pass global constants explicitly
            />

            {/* Budget Key Metrics - COMPACT THREE-COLUMN GRID */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap', // Forces side-by-side distribution on every screen
                    gap: 1, // Tight gaps for space management
                    mb: 4,
                    width: '100%'
                }}
            >
                {/* 1. Budget Pool Card */}
                <Box sx={{ flex: '1 1 33.33%', minWidth: 0 }}>
                    <Card
                        onClick={() => { setIsEditingPool(true); }}
                        elevation={0}
                        sx={{
                            height: '100%',
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.2) 100%)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            border: '1px solid rgba(16, 49, 143, 0.15)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(16, 49, 143, 0.08)',
                                borderColor: 'rgba(16, 49, 143, 0.35)',
                            },
                        }}
                    >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            {/* Icon on Top */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: 'rgba(16, 49, 143, 0.08)', color: '#10318f', mb: 1 }}>
                                <i className="icon-wallet" style={{ fontSize: '14px' }}/>
                            </Box>
                            {/* Amount in Middle */}
                            <Typography sx={{ fontWeight: 800, color: '#10318f', letterSpacing: '-0.3px', fontSize: { xs: '13.5px', sm: '16px', md: '20px' } }}>
                                {(appState.spendingPool || 0).toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: currentCurrencyCode || 'USD',
                                    minimumFractionDigits: 0, // Stripped decimals to keep mobile layout completely un-squished
                                    maximumFractionDigits: 2
                                })}
                            </Typography>
                            {/* Title on Bottom */}
                            <Typography variant="caption" sx={{ color: 'rgba(16, 49, 143, 0.8)', fontWeight: 700, letterSpacing: '0.3px', textTransform: 'uppercase', fontSize: '9px', mt: 0.5 }}>
                                Pool
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>

                {/* 2. Total Expenses Card */}
                <Box sx={{ flex: '1 1 33.33%', minWidth: 0 }}>
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.2) 100%)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            {/* Icon on Top */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '6px', background: 'rgba(0, 0, 0, 0.04)', color: 'text.primary', mb: 1 }}>
                                <i className="icon-fireworks" style={{ fontSize: '14px' }} />
                            </Box>
                            {/* Amount in Middle */}
                            <Typography sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.3px', fontSize: { xs: '13.5px', sm: '16px', md: '20px' } }}>
                                {(totalSpent || 0).toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: currentCurrencyCode || 'USD',
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2
                                })}
                            </Typography>
                            {/* Title on Bottom */}
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.3px', textTransform: 'uppercase', fontSize: '9px', mt: 0.5 }}>
                                Expenses
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>

                {/* 3. Remaining Total Card */}
                <Box sx={{ flex: '1 1 33.33%', minWidth: 0 }}>
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            background: isOverAllocated
                                ? 'linear-gradient(135deg, rgba(211, 47, 47, 0.06) 0%, rgba(255, 255, 255, 0.4) 100%)'
                                : 'linear-gradient(135deg, rgba(46, 125, 50, 0.06) 0%, rgba(255, 255, 255, 0.4) 100%)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            border: isOverAllocated
                                ? '1px solid rgba(211, 47, 47, 0.25)'
                                : '1px solid rgba(46, 125, 50, 0.25)',
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            {/* Icon on Top */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '28px', height: '28px',
                                    borderRadius: '6px',
                                    background: isOverAllocated ? 'rgba(211, 47, 47, 0.08)' : 'rgba(46, 125, 50, 0.08)',
                                    color: isOverAllocated ? 'error.main' : 'success.main',
                                    mb: 1
                                }}
                            >
                                <i className={isOverAllocated ? "icon-sad" : "icon-grin"} style={{ fontSize: '14px' }} />
                            </Box>
                            {/* Amount in Middle */}
                            <Typography sx={{ fontWeight: 800, color: isOverAllocated ? 'error.main' : 'success.main', letterSpacing: '-0.3px', fontSize: { xs: '13.5px', sm: '16px', md: '20px' } }}>
                                {(remainingTotal || 0).toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: currentCurrencyCode || 'USD',
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2
                                })}
                            </Typography>
                            {/* Title on Bottom */}
                            <Typography
                                variant="caption"
                                sx={{
                                    color: isOverAllocated ? 'error.main' : 'success.main',
                                    fontWeight: 700,
                                    letterSpacing: '0.3px',
                                    textTransform: 'uppercase',
                                    fontSize: '9px',
                                    mt: 0.5
                                }}
                            >
                                Balance
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* pie chart */}
            <Box sx={{ mb: 4 }}>

                {/* Outer container */}
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        gap: 2,                     // <-- spacing between chart and legend
                    }}
                >

                    {/* Pie chart container */}
                    <Box sx={{ width: { xs: '100%', sm: '60%' }, height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>

                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={80}
                                    outerRadius={120}
                                    paddingAngle={5}
                                    dataKey="value"
                                    nameKey="name"
                                    onMouseEnter={(_, index) => setActiveSegment(index)}
                                    onMouseLeave={() => setActiveSegment(null)}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={entry.color}
                                            strokeWidth={activeSegment === index ? 5 : 1}
                                            stroke={activeSegment === index ? grey[700] : 'transparent'}
                                        />
                                    ))}
                                </Pie>

                                <Tooltip content={<CustomTooltip />} />

                            </PieChart>
                        </ResponsiveContainer>
                    </Box>

                    {/* Legend OUTSIDE the chart */}
                    <Box
                        sx={{
                            width: { xs: '100%', sm: '40%' },
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        {chartData.map((entry) => (
                            <Box
                                key={entry.name}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                {/* color square */}
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '4px',
                                        bgcolor: entry.color,
                                    }}
                                />

                                {/* category name + small total */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {entry.name}
                                    </Typography>

                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {formatCurrency(entry.value, currentCurrencyCode)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>


                </Box>
            </Box>

            {/* Budget Visualization and Summary - FLEX CONTAINER (main body content) */}
            <Box sx={{ width: '100%', mb: 4 }}>
                {/* Category Cards - FLEX CONTAINER */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {expenseSummary.map(item => (
                        <Box
                            key={item.category}
                            sx={{
                                // Responsive Widths: 1 on xs, 2 on sm, 3 on md, 4 on lg
                                flex: {
                                    xs: '0 1 100%', // 1 card
                                    sm: '0 1 calc(50% - 8px)', // 2 cards
                                    md: '0 1 calc(33.33% - 10.66px)', // 3 cards
                                    lg: '0 1 calc(25% - 12px)', // 4 cards
                                }
                            }}
                        >
                            <CategoryBudgetCard item={item} />
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Expense List and Filter */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: { xs: 1, sm: 0 } }}>
                        Expense History ({filterCategory})
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        {/* Category Filter */}
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Filter Category</InputLabel>
                            <Select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                label="Filter Category"
                            >
                                <MenuItem value="All">All Categories</MenuItem>
                                {CATEGORIES.map(category => (
                                    <MenuItem key={category} value={category}>{category}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Sort Direction Toggle */}
                        <Button
                            onClick={() => setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}
                            variant="outlined"
                            size="small"
                            startIcon={sortDirection === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                            color="inherit"
                        >
                            {sortDirection === 'desc' ? 'Newest' : 'Oldest'}
                        </Button>

                        {/* Sort Key Dropdown */}
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                value={sortKey}
                                onChange={(e) => setSortKey(e.target.value)}
                                label="Sort By"
                            >
                                <MenuItem value="timestamp">Date</MenuItem>
                                <MenuItem value="amount">Amount</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* 1. DESKTOP/TABLET VIEW (Standard Table) - Visible on screens >= md */}
                <TableContainer component={Card} elevation={3} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Table
                        sx={{
                            minWidth: 650,
                            borderCollapse: 'collapse', // 🟢 Cleaner border management
                            '& th, & td': {
                                borderLeft: 'none !important',  // 🟢 Force remove any vertical left lines
                                borderRight: 'none !important', // 🟢 Force remove any vertical right lines
                            }
                        }}
                        aria-label="expense history table"
                    >
                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                            <TableRow>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Item</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Amount ({currentCurrencySymbol})</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredAndSortedExpenses.map((expense) => {
                                console.log("looking at expense : ", expense);

                                const isOverLimit = expense.isOverLimit;

                                // Check if category matches the criteria for showing extra information
                                const showExtraInfo = ['Transportation', 'Shopping', 'Supplies'].includes(expense.category);

                                // Fallbacks for missing details down inside different receipt metadata nodes
                                const displayMerchant = expense.merchant || expense.location?.name;
                                const displayNotes = expense.notes || expense.description;

                                return (
                                    <TableRow
                                        key={expense.id}
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                        }}
                                    >
                                        {/* Date */}
                                        <TableCell
                                            color="text.secondary"
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                width: '83px' // Suggests a fixed conservative bounds for the timestamp string
                                            }}
                                        >
                                            {dayjs(expense.timestamp).format('MMM D, YYYY')}
                                        </TableCell>
                                        {/* Category */}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <i className={CATEGORY_ICONS[expense.category]} style={{ color: expense.color }}/>
                                                <Typography variant="body2">{expense.category}</Typography>
                                            </Box>
                                        </TableCell>
                                        {/* Name & Extra Info Stack */}
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            sx={{
                                                wordBreak: 'break-word',
                                                maxWidth: '300px' // Prevents the cell from stretching infinitely if text has no spaces
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                {expense.itemDescription !== 'Logged Receipt' && (
                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                        {expense.itemDescription}
                                                    </Typography>
                                                )}

                                                {/* Sub-row Meta Block */}
                                                {showExtraInfo && (displayMerchant || displayNotes) && (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                                                        {displayMerchant && (
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                {displayMerchant}
                                                            </Typography>
                                                        )}
                                                        {displayNotes && (
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                                <strong>Notes:</strong> {displayNotes}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        {/* Amount */}
                                        <TableCell
                                            align="right"
                                            sx={{
                                                fontWeight: 600,
                                                color: isOverLimit ? red[700] : 'text.primary',
                                                whiteSpace: 'nowrap',
                                                width: '100px' // Ensures plenty of breathing room for long currency strings
                                            }}
                                        >
                                            {formatCurrency(expense.amount, currentCurrencyCode)}
                                        </TableCell>
                                        {/* Status */}
                                        <TableCell sx={{ width: '90px', p:1 }}>
                                            {isOverLimit ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    <i
                                                        className="icon-warning"
                                                        style={{ height: '24px', color: '#d32f2f' }}
                                                    />
                                                    <Typography sx={{ fontSize: '11px', mt: '3px', textAlign: 'center', color: '#d32f2f' }}>
                                                        Limit {formatCurrency(expense.perUnitLimit, currentCurrencyCode)}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <i className="icon-check" style={{ height: '24px', color: 'rgb(56, 142, 60)' }} />
                                            )}
                                        </TableCell>

                                    </TableRow>
                                );
                            })}
                            {filteredAndSortedExpenses.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        No expenses found for the current filters.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 2. MOBILE VIEW (Stacked Cards) - Visible on screens < md */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {filteredAndSortedExpenses.map((expense) => {

                        console.log("looking at expense : ", expense);

                        // Re-evaluating metadata strings for mobile view layout injection
                        const showExtraInfo = ['Transportation', 'Shopping', 'Supplies'].includes(expense.category);
                        const displayMerchant = expense.merchant || expense.location?.name;
                        const displayNotes = expense.notes || expense.description;

                        return (
                            <Card key={expense.id} sx={{ mb: 1.5, borderRadius: 2, borderLeft: `8px solid ${expense.color}` }}>
                                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <i className={CATEGORY_ICONS[expense.category]} style={{ color: DARK_BLUE }}/>
                                            <Typography variant="caption" sx={{ color: DARK_BLUE }}>
                                                {expense.category}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {dayjs(expense.timestamp).format('MMM D, YYYY')}
                                        </Typography>
                                    </Box>

                                    {expense.itemDescription !== 'Logged Receipt' && (
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                                            {expense.itemDescription}
                                        </Typography>
                                    )}

                                    {/* Extra Detail Injection Point for Mobile UI */}
                                    {showExtraInfo && (displayMerchant || displayNotes) && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1.5, pl: 0.5, borderLeft: '2px solid #e0e0e0' }}>
                                            {displayMerchant && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    <strong>Location:</strong> {displayMerchant}
                                                </Typography>
                                            )}
                                            {displayNotes && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                    <strong>Notes:</strong> {displayNotes}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 2 }}>
                                        {formatCurrency(expense.amount, currentCurrencyCode)}
                                    </Typography>

                                    <Divider sx={{ mb: 1.5 }} />

                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                        {expense.isOverLimit ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 0.5 }}>
                                                <i className="icon-warning" style={{ height: '24px', color: '#d32f2f' }} />
                                                <Typography sx={{ fontSize: '11px', mt: '3px', textAlign: 'center', color: '#d32f2f' }}>
                                                    Limit {formatCurrency(expense.perUnitLimit, currentCurrencyCode)}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <i className="icon-check" style={{ height: '24px', color: 'rgb(56, 142, 60)' }} />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                    {filteredAndSortedExpenses.length === 0 && (
                        <Card elevation={1} sx={{ p: 2, textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                            <Typography variant="body2">No expenses found for the current filters.</Typography>
                        </Card>
                    )}
                </Box>
            </Box>

        </Box>
    )
}

export default UnifiedBudgetPersonalBudget;